/* Measurements page. Graph frame + page engine adapted from the prototype's assets/mp-measurements.js;
 * all customer-facing copy arrives in the section config (config.t). */

/* Shared plot frame: fixed data geometry, responsive lettering, no auto-cropping. */
(function (g) {
  'use strict';
  if (g.LinsoulGraphFrameV22) return;
  const NS = 'http://www.w3.org/2000/svg';
  const MAJOR = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
  const MINOR = [30, 40, 60, 70, 80, 90, 300, 400, 600, 700, 800, 900, 3000, 4000, 6000, 7000, 8000, 9000];
  const tracked = new Set();
  let scheduled = 0;
  function n(tag, attrs = {}, text) {
    const el = document.createElementNS(NS, tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    if (text !== undefined) el.textContent = text;
    return el;
  }
  function fitBleed(svg) {
    const box = svg.closest('.mp-workspace');
    if (!box) return;
    if (innerWidth > 700) {
      box.style.removeProperty('--lsg22-bleed');
      return;
    }
    const current = parseFloat(box.style.getPropertyValue('--lsg22-bleed')) || 0;
    const b = box.getBoundingClientRect();
    const amount = Math.max(0, Math.min(b.left + current, innerWidth - b.right + current) - 2);
    box.style.setProperty('--lsg22-bleed', Math.min(amount, 64) + 'px');
  }
  function setup(svg, mini = false) {
    svg.classList.add('lsg22-chart');
    svg.dataset.graphFrame = 'v22';
    svg.dataset.graphKind = mini ? 'spark' : 'response';
    svg.setAttribute('viewBox', '0 0 1000 500');
    svg.setAttribute('width', '1000');
    svg.setAttribute('height', '500');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    fitBleed(svg);
    tracked.add(svg);
    const width = svg.getBoundingClientRect().width || 1000;
    const fontPx = width < 360 ? 8 : width < 700 ? 9 : 12;
    svg.style.setProperty('--lsg22-font', (fontPx * 1000) / width + 'px');
    return { width: 1000, height: 500, l: 80, r: 30, t: 48, b: 85, displayWidth: width, fontPx };
  }
  function grid(svg, x, y, m, yLabel, xDomain, yDomain, xLabel) {
    const info = setup(svg);
    const L = m.left ?? m.l, R = m.right ?? m.r, T = m.top ?? m.t, B = m.bottom ?? m.b;
    const xd = xDomain || x.domain(), yd = yDomain || y.domain();
    const low = Math.min(...yd), high = Math.max(...yd);
    const group = n('g', { 'data-graph-grid': 'v22', 'aria-hidden': 'true' });
    const unit = 1000 / info.displayWidth, fp = info.fontPx;
    const line = (klass, attrs) => group.append(n('line', { class: klass, ...attrs }));
    const text = (txt, xx, yy, anchor = 'middle', extra = {}) =>
      group.append(n('text', { x: xx, y: yy, 'text-anchor': anchor, class: 'lsg22-axis', ...extra }, txt));
    for (let value = Math.ceil(low / 10) * 10; value <= high + 0.001; value += 10) {
      line('lsg22-grid-y', { x1: L, x2: 1000 - R, y1: y(value), y2: y(value), 'data-db': value });
      text(String(value), L - 5 * unit, y(value) + (fp * unit) / 3, 'end');
    }
    for (const f of MINOR.filter((f) => f >= xd[0] && f <= xd[1]))
      line('lsg22-grid-minor', { x1: x(f), x2: x(f), y1: T, y2: 500 - B, 'data-hz': f });
    const visible =
      info.displayWidth < 350 ? [20, 100, 1000, 20000] : info.displayWidth < 550 ? [20, 100, 1000, 10000, 20000] : MAJOR;
    for (const f of MAJOR.filter((f) => f >= xd[0] && f <= xd[1])) {
      line('lsg22-grid-major', { x1: x(f), x2: x(f), y1: T, y2: 500 - B, 'data-hz': f });
      if (visible.includes(f))
        text(f >= 1000 ? f / 1000 + 'k' : String(f), x(f), 500 - B + (fp + 4) * unit, f === xd[0] ? 'start' : f === xd[1] ? 'end' : 'middle', {
          'data-fr-x-label': f,
        });
    }
    group.append(n('rect', { class: 'lsg22-frame', x: L, y: T, width: 1000 - L - R, height: 500 - T - B }));
    if (yLabel) {
      text(yLabel, 17, (T + 500 - B) / 2, 'middle');
      const yText = group.lastElementChild;
      const labelPx = Math.min(fp, ((500 - T - B) * info.displayWidth) / 1000 / (yLabel.length * 0.55));
      yText.style.setProperty('font-size', labelPx * unit + 'px', 'important');
      const labelX = Math.max(17, labelPx * unit + 2);
      yText.setAttribute('x', labelX);
      yText.setAttribute('transform', 'rotate(-90 ' + labelX + ' ' + (T + 500 - B) / 2 + ')');
    }
    if (xLabel) text(xLabel, (L + 1000 - R) / 2, 500 - 9 * unit);
    svg.append(group);
    svg.dataset.dbStep = '10';
    return group;
  }
  function refresh() {
    scheduled = 0;
    for (const svg of tracked) {
      if (!svg.isConnected) {
        tracked.delete(svg);
        continue;
      }
      fitBleed(svg);
    }
  }
  g.addEventListener('resize', () => {
    if (!scheduled) scheduled = requestAnimationFrame(refresh);
  }, { passive: true });
  function freeze(source, copy) {
    const src = source.querySelectorAll('*'), dst = copy.querySelectorAll('*');
    const props = ['fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'opacity', 'font-family', 'font-size', 'text-anchor'];
    src.forEach((el, i) => {
      if (!dst[i]) return;
      const style = getComputedStyle(el);
      props.forEach((k) => {
        const v = style.getPropertyValue(k);
        if (v) dst[i].setAttribute(k, v);
      });
    });
  }
  g.LinsoulGraphFrameV22 = { setup, grid, freeze, major: MAJOR.slice(), minor: MINOR.slice() };
})(window);

/* Page engine. Source data, reviews and commerce remain independent. */
(function (g) {
  'use strict';
  const instances = new WeakMap();
  let nextId = 0;
  const E = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const icons = {
    close: 'm6 6 12 12M18 6 6 18',
    arrow: 'M4 12h16m-6-6 6 6-6 6',
    eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Zm10-3a3 3 0 1 0 0 6 3 3 0 0 0 0-6',
    hide: 'm3 3 18 18M9 5c6-2 11 4 13 7-1 2-3 4-5 5M6 6c-2 2-3 4-4 6 3 5 7 8 12 7',
    layers: 'm3 7 9-5 9 5-9 5-9-5Zm0 5 9 5 9-5M3 17l9 5 9-5',
    headphones: 'M4 14v-3a8 8 0 0 1 16 0v3M4 12H2v8h5v-8H4Zm16 0h2v8h-5v-8h3Z',
    book: 'M12 6C8 3 5 4 2 5v14c3-1 7-2 10 0 3-2 7-1 10 0V5c-3-1-6-2-10 1Zm0 0v13',
  };
  const icon = (k) => (window.LsIcons && window.LsIcons.svg(k, 'mp-icon')) || '<svg class="mp-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="' + (icons[k] || icons.book) + '"/></svg>';
  const palette = [{ ink: '#2D6A80' }, { ink: '#984F6A' }, { ink: '#5E7139' }];
  const CURVES = ['#00FFFF', '#FFB454', '#B59CFF'];
  const TARGET = '#D8C184';

  function mount(root, options) {
    if (instances.has(root)) return instances.get(root);
    const M = g.LinsoulProductMeasurements, d3 = g.LinsoulD3, FRAME = g.LinsoulGraphFrameV22;
    if (!M || !d3) {
      root.querySelector('[data-role="fallback-notice"]')?.removeAttribute('hidden');
      return { destroy() {} };
    }
    const T = options.strings || {};
    const t = (key, vars) => String(T[key] ?? '').replace(/\{(\w+)\}/g, (_, k) => (vars && vars[k] !== undefined ? vars[k] : ''));
    const uid = 'mp-' + ++nextId, ctrl = new AbortController(), signal = ctrl.signal;
    const q = (s) => root.querySelector(s), qa = (s) => Array.from(root.querySelectorAll(s));
    const catalog = options.catalog, library = options.measurements, context = options.context || {};
    const reviews = options.reviews || { products: {} };
    const model = M.create(catalog, library, options.targets || { profiles: {} }, g.LinsoulEQ, { preview: false, dB: Number(options.alignDb) });
    const rows = options.rows || [];
    let state = {
      selected: model.validateSelection(options.initialProducts || []).selected,
      hidden: new Set(), target: 'none', frequency: 1000, query: '', type: 'all', technology: 'all', includeMissing: false, pickerLimit: 30,
    };
    let stopped = false, redraw = 0, returnFocus = null, plot = {};
    const ids = [...model.records.keys()];
    const colors = new Map(ids.map((id, i) => [id, palette[i % 3]]));

    function color(id) {
      const visible = state.selected.filter((v) => !state.hidden.has(v)), i = visible.indexOf(id);
      return { ...(colors.get(id) || palette[0]), line: i < 0 ? '#ADB3BC' : CURVES[i % 3] };
    }
    function emit(action, detail = {}) {
      root.dispatchEvent(new CustomEvent('linsoul:measurement-action', { bubbles: true, detail: { action, products: state.selected.slice(), ...detail } }));
    }
    function note(text) {
      const el = q('[data-role="message"]');
      el.textContent = text;
      el.hidden = !text;
    }
    function fromURL() {
      if (options.readURL === false) return;
      try {
        const p = new URLSearchParams(g.location.search);
        if (p.has('compare')) {
          const result = model.validateSelection((p.get('compare') || '').split(',').filter(Boolean));
          state.selected = result.selected;
          note(result.issues.length ? t('deeplink_issue') : '');
        }
        state.hidden = new Set((p.get('hidden') || '').split(',').filter((id) => state.selected.includes(id)));
        const target = p.get('target');
        if (target && root.querySelector('[data-role="target"] option[value="' + CSS.escape(target) + '"]')) state.target = target;
      } catch (_) {}
    }
    function syncURL() {
      if (options.writeURL === false) return;
      try {
        const u = new URL(g.location.href);
        state.selected.length ? u.searchParams.set('compare', state.selected.join(',')) : u.searchParams.delete('compare');
        state.target === 'none' ? u.searchParams.delete('target') : u.searchParams.set('target', state.target);
        state.hidden.size ? u.searchParams.set('hidden', [...state.hidden].join(',')) : u.searchParams.delete('hidden');
        g.history.replaceState(null, '', u);
      } catch (_) {}
    }
    const productOf = (id) => model.products.get(model.records.get(id)?.handle);
    const name = (id) => productOf(id)?.name || model.records.get(id)?.name || '';
    function rating(handle) {
      const r = reviews.products?.[handle];
      return r ? M.review(r) : null;
    }
    function photo(p) {
      const url = M.safeURL(p.image);
      return '<span class="mp-photo" aria-hidden="true">' + icon('headphones') + (url ? '<img src="' + E(url) + '" alt="" loading="lazy" decoding="async" width="120" height="120">' : '') + '</span>';
    }
    function techs(p) {
      const list = p.technology || [];
      if (!list.length) return '<span class="mp-muted">' + E(t('tech_missing')) + '</span>';
      return list.map((x) => (options.technologyURL ? '<a class="mp-tech-link" href="' + E(options.technologyURL) + '">' + icon('layers') + E(x) + '</a>' : '<span class="mp-tech-link">' + E(x) + '</span>')).join('');
    }
    function reviewHTML(p) {
      const r = rating(p.handle);
      if (!r || !r.available) return '<span class="mp-review-missing">' + E(!r || r.empty ? t('reviews_none') : t('reviews_unavailable')) + '</span>';
      return '<button type="button" class="mp-review" data-review="' + E(p.handle) + '" aria-label="' + E(t('review_aria', { name: p.name, score: r.display, count: r.count })) + '"><span class="mp-star" aria-hidden="true">★</span><strong>' + r.display + '</strong><span>' + E(t('review_scale')) + '</span><span class="mp-review-count">' + E(t('review_count', { count: r.count })) + '</span></button>';
    }
    const money = (n) => new Intl.NumberFormat(options.locale || 'en-US', { style: 'currency', currency: catalog.currency || 'USD' }).format(n);
    function offerHTML(p) {
      const o = model.offer(p);
      return o ? '<span class="mp-offer-price"><small>' + E(t('price_from')) + '</small>' + money(o.price) + '</span>' : '<span class="mp-muted">' + E(t('price_missing')) + '</span>';
    }
    function formatLabel(p) {
      return p.type === 'Wired IEM' ? t('format_iem') : p.type === 'Headphones' ? t('format_headphones') : p.type || '';
    }
    function optionNote(p) {
      const c = context[p.handle] || {};
      const offer = model.offer(p);
      return c.plugs || (offer && offer.label !== 'Default Title' ? offer.label : '') || t('option_fallback');
    }
    function renderCards() {
      const deck = q('[data-role="products"]');
      deck.dataset.count = state.selected.length;
      deck.innerHTML = state.selected
        .map((id) => {
          const p = productOf(id);
          const url = E(M.safeURL(p.url));
          return '<article class="mp-product-card" style="--product-ink:' + color(id).ink + '" data-product-card="' + E(id) + '">' +
            '<button class="mp-remove" type="button" data-remove="' + E(id) + '" aria-label="' + E(t('remove', { name: p.name })) + '">' + icon('close') + '</button>' +
            '<div class="mp-product-main"><a href="' + url + '" aria-label="' + E(t('view', { name: p.name })) + '">' + photo(p) + '</a><div>' +
            '<p class="mp-edition">' + E([p.brand, formatLabel(p)].filter(Boolean).join(' · ')) + '</p>' +
            '<h2 class="mp-product-name"><a href="' + url + '">' + E(name(id)) + '</a></h2>' + reviewHTML(p) + '</div></div>' +
            '<div class="mp-techs">' + techs(p) + '</div>' +
            '<div class="mp-offer-row">' + offerHTML(p) + '<a class="mp-btn shop" data-product-shop="' + E(p.handle) + '" href="' + url + '">' + E(t('shop_label')) + icon('arrow') + '</a></div>' +
            '<p class="mp-option-note">' + E(optionNote(p)) + '</p></article>';
        })
        .join('');
      const counter = q('[data-role="selection-count"]');
      counter.textContent = state.selected.length === 1 ? t('selected_one') : t('selected_many', { count: state.selected.length, max: M.MAX });
      qa('[data-open-picker]').forEach((b) => {
        b.disabled = state.selected.length >= M.MAX;
      });
      q('[data-role="target"]').disabled = !model.targetsCompatible(state.selected);
    }
    function lineSample(c, dashed = false) {
      return '<svg class="mp-line-sample" viewBox="0 0 24 9" aria-hidden="true"><path d="M1 4.5H23" stroke="' + c + '" stroke-width="3"' + (dashed ? ' stroke-dasharray="5 3"' : '') + '/></svg>';
    }
    function targetName() {
      if (state.target === 'harman') return t('target_harman_legend');
      const label = q('[data-role="target"] option[value="' + CSS.escape(state.target) + '"]')?.dataset.name || state.target;
      return t('target_style_legend', { name: label });
    }
    function renderLegend() {
      q('[data-role="legend"]').innerHTML =
        state.selected
          .map((id) => {
            const hidden = state.hidden.has(id);
            return '<button type="button" class="mp-legend-btn" data-visibility="' + E(id) + '" aria-pressed="' + !hidden + '" aria-label="' + E(t(hidden ? 'show_curve' : 'hide_curve', { name: name(id) })) + '">' + lineSample(color(id).line) + E(name(id)) + icon(hidden ? 'hide' : 'eye') + '</button>';
          })
          .join('') + (state.target !== 'none' ? '<span class="mp-legend-static">' + lineSample(TARGET, true) + E(targetName()) + '</span>' : '');
      q('[data-role="target"]').value = state.target;
    }
    const graphHost = q('.mp-plot-wrap');
    if (graphHost && !q('[data-role="hover"]')) {
      const tip = document.createElement('div');
      tip.className = 'mp-hover';
      tip.dataset.role = 'hover';
      tip.hidden = true;
      tip.setAttribute('aria-live', 'off');
      tip.innerHTML = '<output data-role="frequency-output"></output><div class="mp-readout-rows" data-role="readout"></div>';
      graphHost.append(tip);
    }
    function draw() {
      if (stopped) return;
      const svg = q('[data-role="chart"]'), frame = FRAME.setup(svg), w = frame.width, h = frame.height;
      const margin = { left: frame.l, right: frame.r, top: frame.t, bottom: frame.b };
      const x = d3.scale.log().domain([20, 20000]).range([margin.left, w - margin.right]);
      const y = d3.scale.linear().domain([35, 90]).range([h - margin.bottom, margin.top]);
      plot = { x, y, w, h, margin };
      const s = d3.select(svg);
      s.selectAll('*').remove();
      const names = state.selected.map(name).join(', ');
      const chartTitle = state.selected.length > 1 ? t('chart_compare', { names }) : t('chart_single', { names });
      s.attr('aria-label', chartTitle);
      s.append('title').text(chartTitle);
      const clipId = uid + '-clip';
      s.append('defs').append('clipPath').attr('id', clipId).append('rect').attr({ x: margin.left, y: margin.top, width: w - margin.left - margin.right, height: h - margin.top - margin.bottom });
      FRAME.grid(svg, x, y, margin, t('axis_y'), null, null, t('axis_x'));
      const lines = state.selected.filter((id) => !state.hidden.has(id)).map((id) => ({ key: id, values: model.series(id), color: color(id).line, product: true }));
      if (state.target !== 'none' && model.targetsCompatible(state.selected)) lines.push({ key: 'target', values: model.targetSeries(state.target), color: TARGET, product: false });
      const group = s.append('g').attr('clip-path', 'url(#' + clipId + ')');
      const paths = group.selectAll('path').data(lines, (d) => d.key);
      paths.enter().append('path');
      paths
        .attr('data-curve', (d) => d.key)
        .attr('data-kind', (d) => (d.product ? 'product' : 'target'))
        .attr('data-lsg23-primary', (d) => (d.product && d.color === CURVES[0] ? 'true' : null))
        .attr('fill', 'none')
        .attr('stroke', (d) => d.color)
        .attr('stroke-width', (d) => (d.product ? 3 : 1.8))
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .attr('stroke-dasharray', (d) => (d.product ? null : '6 5'))
        .attr('d', (d) => d3.svg.line().defined((p) => p.value !== null && Number.isFinite(p.value)).x((p) => x(p.hz)).y((p) => y(p.value)).interpolate(d.product ? 'linear' : 'monotone')(d.values));
      const cross = s.append('g').attr('data-crosshair', '');
      cross.append('line').attr({ y1: margin.top, y2: h - margin.bottom, stroke: '#C3D8DE', 'stroke-width': 1, 'stroke-dasharray': '2 4', opacity: 0.55 });
      cross.selectAll('circle').data(lines, (d) => d.key).enter().append('circle').attr({ r: 3.5, stroke: '#15343D', 'stroke-width': 1.5 }).attr('fill', (d) => d.color).attr('data-dot', (d) => d.key);
      const out = lines.some((l) => l.values.some((p) => p.value !== null && (p.value < 35 || p.value > 90)));
      const warn = q('[data-role="range-warning"]');
      warn.hidden = !out;
      warn.textContent = out ? t('range_warning') : '';
      const empty = q('[data-role="plot-empty"]');
      empty.hidden = lines.length > 0;
      empty.querySelector('h3').textContent = state.selected.length ? t('hidden_title') : t('empty_title');
      empty.querySelector('p').textContent = state.selected.length ? t('hidden_text') : t('empty_text');
      empty.querySelector('button').hidden = state.selected.length > 0;
      renderReadout();
    }
    const freq = (f) => (f >= 1000 ? Number((f / 1000).toFixed(2)) + ' kHz' : Math.round(f) + ' Hz');
    function renderReadout() {
      q('[data-role="frequency-output"]').textContent = freq(state.frequency);
      q('[data-role="hover"]').hidden = !state.inspect;
      const value = (v) => (v === null ? t('no_sample') : v.toFixed(1) + ' dB');
      const vis = state.selected.filter((id) => !state.hidden.has(id));
      q('[data-role="readout"]').innerHTML =
        vis.map((id) => '<span class="mp-readout-value"><i style="background:' + color(id).line + '"></i>' + E(name(id)) + '<strong>' + value(model.value(id, state.frequency)) + '</strong></span>').join('') +
        (state.target !== 'none' ? '<span class="mp-readout-value"><i style="background:' + TARGET + '"></i>' + E(t('reference_short')) + '<strong>' + value(model.targetValue(state.target, state.frequency)) + '</strong></span>' : '');
      const svg = q('[data-role="chart"]');
      if (!plot.x) return;
      const cross = d3.select(svg).select('[data-crosshair]').attr('visibility', state.inspect ? 'visible' : 'hidden');
      cross.select('line').attr({ x1: plot.x(state.frequency), x2: plot.x(state.frequency) });
      const at = (d) => (d.product ? model.value(d.key, state.frequency) : model.targetValue(state.target, state.frequency));
      cross.selectAll('circle').attr('cx', plot.x(state.frequency)).attr('cy', (d) => {
        const v = at(d);
        return v === null ? -100 : plot.y(v);
      }).style('display', (d) => {
        const v = at(d);
        return v === null || v < 35 || v > 90 ? 'none' : null;
      });
    }
    function renderMode() {
      const count = state.selected.length, comparing = count > 1;
      root.dataset.selectionMode = comparing ? 'comparison' : count ? 'single' : 'empty';
      const set = (role, text) => {
        const el = q('[data-role="' + role + '"]');
        if (el) el.textContent = text;
      };
      const label = q('.mp-selection-label');
      if (label) label.hidden = !count;
      set('selection-label', comparing ? t('label_compare') : t('label_single'));
      const counter = q('[data-role="selection-count"]');
      if (counter) counter.hidden = !comparing;
      const detail = q('.mp-details');
      if (detail) detail.hidden = !count;
      set('details-heading', comparing ? t('details_compare') : t('details_single'));
      const exportButton = q('[data-export]');
      if (exportButton) exportButton.disabled = !count;
    }
    function cell(field, p) {
      const c = context[p.handle] || {};
      const plain = (v) => (v ? E(v) : '<span class="mp-muted">' + E(t('not_supplied')) + '</span>');
      switch (field) {
        case 'rating': return reviewHTML(p);
        case 'technology': return techs(p);
        case 'configuration': return plain(c.cfg) + (c.lay ? '<span class="mp-muted">' + E(c.lay) + '</span>' : '');
        case 'layout': return plain(c.lay);
        case 'connector': return plain(c.con);
        case 'plugs': return plain(c.plugs);
        case 'brand': return plain(p.brand);
        case 'format': return plain(formatLabel(p));
        case 'price':
          return offerHTML(p) + (t('price_note') ? '<span class="mp-muted">' + E(t('price_note')) + '</span>' : '') +
            '<a class="mp-btn shop" data-product-shop="' + E(p.handle) + '" style="margin-top:10px" href="' + E(M.safeURL(p.url)) + '">' + E(t('shop_label')) + icon('arrow') + '</a>';
        default: return '';
      }
    }
    function renderTable() {
      const container = q('[data-role="comparison-table"]');
      if (!state.selected.length) {
        container.innerHTML = '<p class="mp-static-fallback">' + E(t('table_empty')) + '</p>';
        return;
      }
      const comparing = state.selected.length > 1;
      const heading = comparing ? t('details_compare') : t('details_single');
      container.innerHTML =
        '<div class="mp-table-scroll" tabindex="0" role="region" aria-label="' + E(heading) + '"><table class="mp-table"><caption class="mp-sr">' + E(heading) + '</caption><thead><tr><th scope="col">' + E(comparing ? t('label_compare') : t('details_single')) + '</th>' +
        state.selected.map((id) => '<th scope="col"><span style="color:' + color(id).ink + '">' + E(name(id)) + '</span></th>').join('') + '</tr></thead><tbody>' +
        rows.map((row) => '<tr><th scope="row">' + E(row.label) + '</th>' + state.selected.map((id) => '<td>' + cell(row.field, productOf(id)) + '</td>').join('') + '</tr>').join('') +
        '</tbody></table></div>';
    }
    function renderSources() {
      const div = q('[data-role="source-records"]');
      if (!div) return;
      const unknown = t('src_unknown');
      div.innerHTML =
        state.selected
          .map((id) => {
            const m = model.records.get(id);
            const row = (label, value) => (label ? '<dt>' + E(label) + '</dt><dd>' + E(value || unknown) + '</dd>' : '');
            return '<article class="mp-source-record"><h3>' + E(name(id)) + '</h3><dl>' + row(t('src_provider'), m.provider) + row(t('src_rig'), m.rig) + row(t('src_processing'), m.processing) + row(t('src_channel'), m.channel) + row(t('src_date'), m.sourceDate) + '</dl></article>';
          })
          .join('') || '<p>' + E(t('src_empty')) + '</p>';
    }
    function restoreFocus(key, value) {
      qa('[' + key + ']').find((x) => x.getAttribute(key) === value)?.focus({ preventScroll: true });
    }
    function render() {
      if (!model.targetsCompatible(state.selected)) state.target = 'none';
      renderMode();
      renderCards();
      renderLegend();
      renderTable();
      renderSources();
      draw();
      syncURL();
    }
    function pickerTechs() {
      const list = model.picker({ type: state.type, includeMissing: state.includeMissing });
      const ts = [...new Set(list.flatMap((x) => x.product.technology || []))].sort();
      if (!ts.includes(state.technology)) state.technology = 'all';
      const select = q('[data-role="picker-tech"]');
      select.innerHTML = '<option value="all">' + E(t('picker_any_tech')) + '</option>' + ts.map((x) => '<option value="' + E(x) + '">' + E(x) + '</option>').join('');
      select.value = state.technology;
    }
    function renderPicker() {
      pickerTechs();
      const items = model.picker(state), visible = items.slice(0, state.pickerLimit), wrap = q('[data-role="picker-results"]');
      const summaryKey = (state.includeMissing ? 'picker_summary_all' : 'picker_summary_graph') + (items.length === 1 ? '_one' : '');
      q('[data-role="picker-summary"]').textContent = t(summaryKey, { count: items.length, selected: state.selected.length, max: M.MAX });
      q('[data-role="picker-more"]').hidden = items.length <= state.pickerLimit;
      if (!items.length) {
        wrap.innerHTML = '<div class="mp-no-results"><h3>' + E(state.type === 'headphones' && !state.includeMissing ? t('picker_none_headphones') : t('picker_none')) + '</h3><p>' + E(state.includeMissing ? t('picker_hint_all') : t('picker_hint_graph')) + '</p>' +
          (!state.includeMissing ? '<button class="mp-btn" type="button" data-show-missing>' + E(t('picker_show_missing')) + '</button>' : '') + '</div>';
        return;
      }
      wrap.innerHTML = visible
        .map(({ product: p, measurement: m }) => {
          const added = m && state.selected.includes(m.id), compat = m && model.compatible(state.selected, m.id);
          const label = added ? t('picker_added') : !m ? t('picker_no_graph') : !compat ? t('picker_other_fixture') : state.selected.length >= M.MAX ? t('picker_limit') : t('picker_add');
          const disabled = added || !m || !compat || state.selected.length >= M.MAX;
          return '<article class="mp-picker-row">' + photo(p) + '<div><h3>' + E(p.name) + '</h3><p>' + E([formatLabel(p), (p.technology || []).join(' + ')].filter(Boolean).join(' · ')) + '</p><span class="mp-picker-status ' + (!m ? 'missing' : '') + '">' + E(m ? t('picker_has_graph') : t('picker_missing')) + '</span></div>' +
            '<div class="mp-picker-actions"><a class="mp-link" href="' + E(M.safeURL(p.url)) + '">' + E(t('picker_view')) + '</a><button class="mp-btn ' + (disabled ? '' : 'dark') + '" type="button" data-add="' + E(m?.id || '') + '" ' + (disabled ? 'disabled' : '') + '>' + E(label) + '</button></div></article>';
        })
        .join('');
    }
    function openDialog(role, from) {
      const d = q('[data-role="' + role + '"]');
      returnFocus = from || document.activeElement;
      if (!d.open) d.showModal();
      if (role === 'picker') {
        renderPicker();
        q('[data-role="picker-query"]').focus();
      } else d.querySelector('button')?.focus();
    }
    function openReview(handle, from) {
      const r = rating(handle), p = model.products.get(handle);
      if (!r?.available || !p) return;
      q('[data-role="review-title"]').textContent = p.name;
      q('[data-role="review-body"]').innerHTML =
        '<div class="mp-review-score"><strong>' + r.display + '</strong><div><p>' + E(t('review_out_of')) + '</p><p>' + E(t('review_customer_count', { count: r.count })) + '</p></div></div>' +
        '<a class="mp-btn shop" data-product-shop="' + E(handle) + '" href="' + E(M.safeURL(r.sourceURL)) + '">' + E(t('review_read')) + icon('arrow') + '</a>';
      openDialog('review-dialog', from);
    }
    function exportGraph() {
      const original = q('[data-role="chart"]');
      if (!state.selected.length) return;
      const clone = original.cloneNode(true), ns = 'http://www.w3.org/2000/svg', w = plot.w, h = plot.h, extra = 80 + state.selected.length * 18;
      clone.setAttribute('xmlns', ns);
      clone.setAttribute('viewBox', '0 0 ' + w + ' ' + (h + extra));
      clone.setAttribute('width', w);
      clone.setAttribute('height', h + extra);
      FRAME.freeze(original, clone);
      clone.removeAttribute('class');
      clone.removeAttribute('style');
      const bg = document.createElementNS(ns, 'rect');
      bg.setAttribute('width', w);
      bg.setAttribute('height', h + extra);
      bg.setAttribute('fill', '#16171A');
      clone.insertBefore(bg, clone.firstChild);
      clone.querySelector('[data-crosshair]')?.remove();
      let y = h + 20;
      const txt = (value, size = 11) => {
        if (!value) return;
        const n = document.createElementNS(ns, 'text');
        n.setAttribute('x', 20);
        n.setAttribute('y', y);
        n.setAttribute('fill', '#D8E7E9');
        n.setAttribute('font-family', 'Arial, sans-serif');
        n.setAttribute('font-size', size);
        n.textContent = value;
        clone.appendChild(n);
        y += 18;
      };
      txt(state.selected.length > 1 ? t('export_compare') : t('export_single'), 14);
      state.selected.forEach((id) => txt(name(id) + ' — ' + model.records.get(id).provider));
      if (state.target !== 'none') txt(targetName());
      txt(t('export_norm'));
      const metadata = document.createElementNS(ns, 'metadata');
      metadata.textContent = JSON.stringify({
        normalization: 'source(f)-source(1000 Hz)+' + (Number.isFinite(Number(options.alignDb)) ? Number(options.alignDb) : 70), displayRange: [35, 90], frequencyRange: [20, 20000], target: state.target,
        products: state.selected.map((id) => {
          const m = model.records.get(id);
          return { id, name: name(id), provider: m.provider, rig: m.rig, channel: m.channel, processing: m.processing, sourceDate: m.sourceDate, url: m.sourceURL };
        }),
        reference: state.target !== 'none' ? library.reference?.sourceURL : null,
      });
      clone.appendChild(metadata);
      const xml = '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(clone);
      const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = state.selected.length > 1 ? 'Linsoul-product-comparison.svg' : 'Linsoul-product-measurement.svg';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      emit('export');
    }

    fromURL();
    root.addEventListener('click', (ev) => {
      const b = ev.target.closest('button,a');
      if (!b || !root.contains(b)) return;
      if (b.hasAttribute('data-open-picker')) {
        openDialog('picker', b);
        emit('open-picker');
      } else if (b.hasAttribute('data-open-sources')) {
        renderSources();
        openDialog('sources-dialog', b);
      } else if (b.hasAttribute('data-close-dialog')) b.closest('dialog').close();
      else if (b.hasAttribute('data-add')) {
        const id = b.dataset.add;
        if (!b.disabled && model.compatible(state.selected, id) && state.selected.length < M.MAX) {
          state.selected.push(id);
          state.hidden.delete(id);
          note('');
          render();
          renderPicker();
          restoreFocus('data-add', id);
          emit('add-product', { id });
        }
      } else if (b.hasAttribute('data-remove')) {
        const id = b.dataset.remove;
        state.selected = state.selected.filter((x) => x !== id);
        state.hidden.delete(id);
        render();
        q('[data-open-picker]')?.focus({ preventScroll: true });
        emit('remove-product', { id });
      } else if (b.hasAttribute('data-visibility')) {
        const id = b.dataset.visibility;
        state.hidden.has(id) ? state.hidden.delete(id) : state.hidden.add(id);
        renderLegend();
        draw();
        syncURL();
        restoreFocus('data-visibility', id);
        emit('toggle-curve', { id });
      } else if (b.hasAttribute('data-review')) openReview(b.dataset.review, b);
      else if (b.hasAttribute('data-export')) exportGraph();
      else if (b.hasAttribute('data-show-missing')) {
        state.includeMissing = true;
        q('[data-role="picker-missing"]').checked = true;
        renderPicker();
        q('[data-role="picker-query"]').focus();
      } else if (b.hasAttribute('data-picker-more')) {
        state.pickerLimit += 30;
        renderPicker();
      } else if (b.hasAttribute('data-clear')) {
        state.selected = [];
        state.hidden.clear();
        state.target = 'none';
        render();
        q('[data-open-picker]')?.focus({ preventScroll: true });
      } else if (b.hasAttribute('data-product-shop')) emit('product-click', { handle: b.dataset.productShop });
    }, { signal });
    root.addEventListener('input', (ev) => {
      if (ev.target.dataset.role === 'picker-query') {
        state.query = ev.target.value;
        state.pickerLimit = 30;
        renderPicker();
      }
    }, { signal });
    root.addEventListener('change', (ev) => {
      const role = ev.target.dataset.role;
      if (role === 'target') {
        state.target = ev.target.value;
        if (!model.targetsCompatible(state.selected)) state.target = 'none';
        renderLegend();
        draw();
        syncURL();
        emit('target-change', { target: state.target });
      } else if (role === 'picker-type') {
        state.type = ev.target.value;
        state.technology = 'all';
        state.pickerLimit = 30;
        renderPicker();
      } else if (role === 'picker-tech') {
        state.technology = ev.target.value;
        state.pickerLimit = 30;
        renderPicker();
      } else if (role === 'picker-missing') {
        state.includeMissing = ev.target.checked;
        state.pickerLimit = 30;
        renderPicker();
      }
    }, { signal });
    root.addEventListener('error', (ev) => {
      if (ev.target.tagName === 'IMG') ev.target.hidden = true;
    }, { capture: true, signal });
    qa('dialog').forEach((d) => {
      d.addEventListener('close', () => {
        const el = returnFocus;
        if (el?.isConnected) el.focus({ preventScroll: true });
        else q('[data-open-picker]')?.focus({ preventScroll: true });
      }, { signal });
      d.addEventListener('click', (ev) => {
        if (ev.target !== d) return;
        const r = d.getBoundingClientRect();
        if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) d.close();
      }, { signal });
    });
    const svg = q('[data-role="chart"]');
    svg.addEventListener('pointermove', (ev) => {
      if (ev.pointerType === 'touch' || !plot.x) return;
      const r = svg.getBoundingClientRect(), px = ((ev.clientX - r.left) * plot.w) / r.width;
      if (px < plot.margin.left || px > plot.w - plot.margin.right) return;
      state.inspect = true;
      state.frequency = plot.x.invert(px);
      renderReadout();
    }, { signal });
    svg.addEventListener('pointerleave', () => {
      state.inspect = false;
      renderReadout();
    }, { signal });
    svg.setAttribute('tabindex', '0');
    svg.addEventListener('keydown', (ev) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape'].includes(ev.key)) return;
      ev.preventDefault();
      state.inspect = ev.key !== 'Escape';
      if (state.inspect)
        state.frequency = Math.max(20, Math.min(20000, ev.key === 'Home' ? 20 : ev.key === 'End' ? 20000 : state.frequency * Math.pow(2, (ev.key === 'ArrowRight' ? 1 : -1) / 12)));
      renderReadout();
    }, { signal });
    svg.addEventListener('blur', () => {
      state.inspect = false;
      renderReadout();
    }, { signal });
    let observer;
    if (g.ResizeObserver) {
      observer = new ResizeObserver(() => {
        cancelAnimationFrame(redraw);
        redraw = requestAnimationFrame(draw);
      });
      observer.observe(svg);
    }
    function destroy() {
      if (stopped) return;
      stopped = true;
      ctrl.abort();
      observer?.disconnect();
      cancelAnimationFrame(redraw);
      qa('dialog[open]').forEach((d) => d.close());
      root.classList.remove('mp-enhanced');
      instances.delete(root);
    }
    const api = { destroy, refresh: render, state, model, exportGraph };
    instances.set(root, api);
    root.classList.add('mp-enhanced');
    render();
    q('[data-role="fallback-notice"]')?.setAttribute('hidden', '');
    return api;
  }

  g.LsMeasurementsPage = { mount, instances };
})(window);

/* Store data → engine inputs. */
(function (g) {
  'use strict';
  const readJSON = (el) => (el ? JSON.parse(el.textContent || 'null') : null);
  const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

  async function sectionJSON(url, selector) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(url + ' returned ' + response.status);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return readJSON(doc.querySelector(selector));
  }

  async function loadCatalog(config) {
    if (!config.catalogURL) return [];
    const pageURL = (page) => config.catalogURL + '&page=' + page;
    try {
      const first = await sectionJSON(pageURL(1), '[data-ls-measure-catalog]');
      if (!first) return [];
      const rest = [];
      for (let page = 2; page <= first.pages; page += 1) rest.push(sectionJSON(pageURL(page), '[data-ls-measure-catalog]'));
      return [first, ...(await Promise.all(rest))].flatMap((p) => (p ? p.products : []));
    } catch (error) {
      console.warn(error);
      return [];
    }
  }

  async function loadOptional(url, selector) {
    try {
      return selector ? await sectionJSON(url, selector) : await fetch(url).then((r) => r.json());
    } catch (error) {
      console.warn(error);
      return null;
    }
  }

  function formatOf(p, config) {
    if (p.fmt) return p.fmt;
    const type = '|' + (p.pt || '') + '|';
    if (p.pt && config.iemTypes.includes(type)) return 'Wired IEM';
    if (p.pt && config.headphoneTypes.includes(type)) return 'Headphones';
    return p.pt || '';
  }

  function toProduct(p, config) {
    const variants = (p.v || []).map(([label, price, available]) => ({ label, price, available }));
    const anyAvailable = variants.some((v) => v.available);
    return {
      handle: p.h,
      name: p.n,
      brand: p.b,
      type: formatOf(p, config),
      technology: asArray(p.tech),
      configuration: p.cfg || null,
      url: new URL('/products/' + p.h, g.location.origin).href,
      image: p.i || '',
      status: 'active',
      published: true,
      fullProductVariant: true,
      approval: 'approved',
      liveValidated: true,
      variants: variants.map((v) => ({
        label: v.label,
        price: v.price,
        eligible: true,
        orderable: anyAvailable ? v.available : true,
        available: anyAvailable ? v.available : true,
      })),
    };
  }

  async function start(scope) {
    const root = scope.querySelector('[data-linsoul-measurements]');
    if (!root || g.LsMeasurementsPage.instances.has(root)) return;
    const config = readJSON(scope.querySelector('[data-ls-measurements-config]'));
    config.iemTypes = '|' + (config.iemTypes || '') + '|';
    config.headphoneTypes = '|' + (config.headphoneTypes || '') + '|';
    const [raw, feed, target, styles] = await Promise.all([
      loadCatalog(config),
      loadOptional(config.measurementsURL, '[data-ls-measurements]'),
      config.rigFamily === '711' ? loadOptional(config.targetURL) : null,
      config.rigFamily === '711' && config.styleTargets ? loadOptional(config.stylesURL) : null,
    ]);
    const products = new Map();
    const context = {};
    const reviews = {};
    const add = (p) => {
      if (!p || !p.h || products.has(p.h)) return;
      const product = toProduct(p, config);
      products.set(p.h, product);
      context[p.h] = { cfg: p.cfg || '', lay: p.lay || '', con: p.con || '', plugs: p.plugs || '' };
      if (p.r) {
        const [average, scaleMax, count] = p.r.map(Number);
        if (average > 0 && count > 0 && scaleMax === 5) reviews[p.h] = { status: 'live', average, scaleMin: 1, scaleMax: 5, count, sourceURL: product.url };
      }
    };
    raw.forEach(add);
    (feed || []).forEach((m) => add(m.p));

    const records = (feed || [])
      .filter((m) => products.has(m.h))
      .map((m) => {
        const product = products.get(m.h);
        return {
          id: m.id,
          handle: m.h,
          name: product.name,
          kind: 'measurement-derived',
          form: product.type === 'Headphones' ? 'over-ear' : 'in-ear',
          rigFamily: config.rigFamily,
          provider: m.provider || config.defaultProvider || '',
          rig: m.rig || '',
          sourceURL: product.url,
          channel: m.channel || '',
          processing: m.smoothing || '',
          sourceDate: m.date || '',
          points: m.pts,
          approved: true,
        };
      });

    const reference = target?.reference ? { ...target.reference, approved: true } : null;
    const library = { form: 'in-ear', rigFamily: config.rigFamily, normalizationHz: 1000, reference, products: records };
    const defaultIds = (config.defaultHandles || [])
      .map((h) => records.find((r) => r.handle === h)?.id)
      .filter(Boolean);

    g.LsMeasurementsPage.mount(root, {
      catalog: { currency: config.currency, products: [...products.values()] },
      measurements: library,
      targets: styles || { profiles: {} },
      reviews: { products: reviews },
      context,
      strings: config.t,
      rows: config.rows,
      locale: config.locale,
      technologyURL: config.technologyURL,
      alignDb: config.alignDb,
      initialProducts: defaultIds.length ? defaultIds : records.slice(0, 1).map((r) => r.id),
      readURL: !config.designMode,
      writeURL: !config.designMode,
    });
  }

  const clearLoading = (scope) => scope.querySelectorAll('[data-ls-mp-loading], .ls-mp-skel-card').forEach((el) => el.remove());
  const boot = (scope) =>
    start(scope)
      .then(() => clearLoading(scope))
      .catch((error) => {
        console.error(error);
        clearLoading(scope);
        scope.querySelector('[data-role="fallback-notice"]')?.removeAttribute('hidden');
      });
  const ready = () => document.querySelectorAll('.ls-measurements').forEach(boot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('.ls-measurements').forEach(boot));
  document.addEventListener('shopify:section:unload', (e) =>
    e.target.querySelectorAll('[data-linsoul-measurements]').forEach((root) => g.LsMeasurementsPage.instances.get(root)?.destroy())
  );
})(window);
